import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import PasswordStrengthBar from 'react-password-strength-bar'

import { TextField, Button, CircularProgress, Typography } from '@mui/material'

import {verifyChangePasswordRequest, updateUserPassword } from '/providers/auth-api'
import { AppContext } from '/providers/app-context'

const PasswordResetForm = ({ token }) => {

  const navigate = useNavigate()
  const {apiSource, setProcessing} = useContext(AppContext)
  const [stage,setStage] = useState("verifying")
  const [password,setPassword] = useState("")
  const [passwordScore, setPasswordScore] = useState(0)
  const [passwordError, setPasswordError] = useState('')

  useEffect(() => {
    verifyToken()
  },[token])

  const verifyToken = async () => {
    const response = await verifyChangePasswordRequest({apiSource,token})
    setStage(response.verified ? "form" : "rejected")
  }

  const passwordValidationMessage = (value, { requireStrength = false, score } = {}) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Password is required.'
    }
    if (trimmed.length < 8) {
      return 'Password must be at least 8 characters.'
    }
    const effectiveScore = typeof score === 'number' ? score : passwordScore
    if (requireStrength && effectiveScore < 3) {
      return 'Choose a stronger password before continuing.'
    }
    return ''
  }

  const handlePasswordScoreChange = (score) => {
    setPasswordScore(score)
    if (passwordError) {
      setPasswordError(passwordValidationMessage(password, { requireStrength: true, score }))
    }
  }

  const handlePasswordInputChange = (nextValue) => {
    setPassword(nextValue)
    if (passwordError) {
      setPasswordError(passwordValidationMessage(nextValue, { requireStrength: true }))
    }
  }

  const validatePasswordField = (value = password, options = {}) => {
    const message = passwordValidationMessage(value, options)
    setPasswordError(message)
    return !message
  }

  const changePassword = async () => {
    if (!passwordError) {
      setProcessing(true)
      const result = await updateUserPassword({apiSource,token, password})
      setProcessing(false)
      if (result.ok) {
        setStage("confirmed")
      } else {
        setStage("rejected")
      }
    }
  }

  if (stage === "verifying") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Verifying reset request</Typography>
      Please wait a moment whilst we verify your password reset request...
      <div className='flex flex-row justify-around'>
        <CircularProgress/>
      </div>
    </div>
  )

  if (stage === "form") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Choose a new password</Typography>
        <TextField
          label='Password'
          type='password'
          value={password}
          onChange={e => handlePasswordInputChange(e.target.value)}
          onBlur={() => validatePasswordField(password, { requireStrength: true })}
          fullWidth
          size='small'
          error={Boolean(passwordError)}
          helperText={passwordError}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <div className='opacity-60 text-sm mt-5'>Password strength (good or strong required)</div>
        <PasswordStrengthBar password={password} onChangeScore={handlePasswordScoreChange} />
        
        <Button onClick={changePassword} variant='contained'>Set new password</Button>

    </div>
  )

  if (stage === "confirmed") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Password changed</Typography>
      Your password has been updated. Please return to the login page
      <Button onClick={() => navigate("/")} variant='contained'>Return to login page</Button>
    </div>
  )

  if (stage === "rejected") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Unable to verify request</Typography>
      Please return to the login page and submit a new password reset request
      <Button onClick={() => navigate("/")} variant='contained'>Return to login page</Button>
    </div>
  )
}

export default PasswordResetForm
